<template>
	<div v-if="chatbotId" class="ai-chatbot">
		<div class="ai-chatbot-btn" @click="toggle">
			<span class="ai-chatbot-btn__pulse" />
			<span class="ai-chatbot-btn__label">AI</span>
		</div>

		<!-- 聊天視窗（用 display 控制顯隱，保留對話狀態） -->
		<teleport-body>
			<div
				v-if="isMobile && isOpen"
				class="ai-chatbot-overlay"
				@click="close"
			/>
			<div
				class="ai-chatbot-wrapper"
				:class="{
					'ai-chatbot-wrapper--open': isOpen,
					'ai-chatbot-wrapper--mobile': isMobile,
				}"
			>
				<div class="ai-chatbot-wrapper__container">
					<button class="ai-chatbot-wrapper__close" @click="close">
						&#x2715;
					</button>
					<div class="ai-chatbot-wrapper__window">
						<!-- 載入中提示 -->
						<div v-if="iframeLoading" class="ai-chatbot-loading">
							<div class="ai-chatbot-loading__spinner" />
							<span class="ai-chatbot-loading__text">AI 客服載入中...</span>
						</div>
						<iframe
							v-if="loaded"
							ref="chatIframe"
							class="ai-chatbot-wrapper__iframe"
							:class="{ 'ai-chatbot-wrapper__iframe--hidden': iframeLoading }"
							:src="iframeSrc"
							frameborder="0"
							allow="microphone"
							@load="onIframeLoad"
						/>
					</div>
				</div>
			</div>
		</teleport-body>
	</div>
</template>

<script>
// Vue 2 沒有 Teleport，用簡易元件將內容掛載到 body
const TeleportBody = {
	name: 'TeleportBody',
	mounted() {
		document.body.appendChild(this.$el)
	},
	beforeDestroy() {
		if (this.$el.parentNode) {
			this.$el.parentNode.removeChild(this.$el)
		}
	},
	render(h) {
		return h('div', { style: { display: 'contents' } }, this.$slots.default)
	},
}

export default {
	name: 'MyAiChatBot',
	components: { TeleportBody },
	props: {
		chatbotId: {
			type: String,
			required: true,
		},
	},
	data() {
		return {
			isOpen: false,
			loaded: false,
			iframeLoading: true,
		}
	},
	computed: {
		iframeSrc() {
			return `https://www.chatbase.co/chatbot-iframe/${this.chatbotId}`
		},
		isMobile() {
			return this.$getMobile()
		},
	},
	methods: {
		toggle() {
			this.isOpen = !this.isOpen
			// 第一次開啟時才載入 iframe
			if (this.isOpen && !this.loaded) {
				this.loaded = true
				this.iframeLoading = true
			}
		},
		close() {
			this.isOpen = false
		},
		onIframeLoad() {
			this.iframeLoading = false
		},
	},
}
</script>

<style lang="scss">
.ai-chatbot-btn {
	cursor: pointer;
	margin-left: 12px;
	display: flex;
	justify-content: center;
	align-items: center;
	position: relative;
	width: 38px;
	height: 38px;
	background: rgba(255, 255, 255, 0.2);
	border: solid rgba(255, 255, 255, 0.3) 2px;
	border-radius: 8px;
	transition: 0.2s;

	&:hover {
		background: rgba(255, 255, 255, 0.35);
	}

	&:active {
		opacity: 0.5;
	}

	// 呼吸光暈特效
	&__pulse {
		position: absolute;
		inset: -3px;
		border-radius: 10px;
		background: rgba(237, 180, 119, 0.4);
		animation: ai-pulse 2s ease-in-out infinite;
		pointer-events: none;
	}

	&__label {
		position: relative;
		color: #fff;
		font-size: 0.8rem;
		font-weight: 900;
		letter-spacing: 0.5px;
		line-height: 1;
	}
}

@keyframes ai-pulse {
	0%,
	100% {
		opacity: 0;
		transform: scale(1);
	}
	50% {
		opacity: 1;
		transform: scale(1.15);
	}
}

.ai-chatbot-overlay {
	position: fixed;
	top: 0;
	left: 0;
	width: 100vw;
	height: 100vh;
	background: rgba(0, 0, 0, 0.5);
	z-index: 9996;
}

.ai-chatbot-wrapper {
	position: fixed;
	bottom: 20px;
	right: 20px;
	z-index: 9997;
	display: none;

	&--open {
		display: block;
	}

	&--mobile.ai-chatbot-wrapper--open {
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
	}

	&__container {
		position: relative;
	}

	// 關閉按鈕貼齊視窗右上角外側
	&__close {
		position: absolute;
		top: -14px;
		right: -14px;
		background: #383838;
		border: 2px solid #fff;
		color: #fff;
		font-size: 0.7rem;
		cursor: pointer;
		width: 28px;
		height: 28px;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
		transition: background 0.2s;
		z-index: 1;

		&:hover {
			background: #555;
		}

		.ai-chatbot-wrapper--mobile & {
			top: 8px;
			right: 8px;
			border-color: transparent;
			background: rgba(0, 0, 0, 0.5);
		}
	}

	&__window {
		width: 400px;
		height: 550px;
		background: #fff;
		border-radius: 12px;
		box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
		overflow: hidden;
		position: relative;

		.ai-chatbot-wrapper--mobile & {
			width: 100vw;
			height: 100vh;
			border-radius: 0;
		}
	}

	&__iframe {
		width: 100%;
		height: 100%;
		border: none;
		display: block;

		&--hidden {
			visibility: hidden;
		}
	}
}

// 載入中畫面
.ai-chatbot-loading {
	position: absolute;
	inset: 0;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	background: #fff;
	z-index: 1;

	&__spinner {
		width: 36px;
		height: 36px;
		border: 3px solid #eee;
		border-top-color: #383838;
		border-radius: 50%;
		animation: ai-spin 0.8s linear infinite;
	}

	&__text {
		margin-top: 14px;
		color: #999;
		font-size: 0.85rem;
	}
}

@keyframes ai-spin {
	to {
		transform: rotate(360deg);
	}
}
</style>
